import styled from "@emotion/styled";
import { MobileProfileBar } from "components/NavbarV2/MobileProfileBar";
import { NavbarV2 } from "components/NavbarV2/NavbarV2";
import { MOBILE_NAVBAR_HEIGHT, NavbarV2Mobile } from "components/NavbarV2/NavbarV2Mobile";
import { ProfileActionsContent } from "components/NavbarV2/elements/ProfileActions/elements/ProfileActionsContent";
import { SideNav } from "components/NavbarV2/elements/SideNav";
import { Socials } from "components/NavbarV2/elements/Socials";
import { MobileBottomSheet } from "components/general/MobileBottomSheet/MobileBottomSheet";
import { BEBOP_ROUTES, Z_INDICES } from "config/consts";
import {
  DeviceType,
  MOBILE_WIDTH,
  TABLET_WIDTH,
  useDeviceType,
  useIsTablet,
} from "hooks/useDeviceType";
import { useToggleIntercom } from "hooks/useToggleIntercom";
import React, { PropsWithChildren, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GlobalStore } from "store/global/GlobalStore";
import { withIsVisible } from "../../utils/reactUtils";
import { BebopBWhite, BebopWhiteIcon } from "../Icons";
import { css } from "@emotion/react";
import { Banner } from "components/BannerSection/Banner";
import { ReactComponent as TradeBgTopRight } from "../../img/backgrounds/trade-bg-top-right.svg";
import { ReactComponent as TradeBgBottomLeft } from "../../img/backgrounds/trade-bg-bottom-left.svg";
import { heightAdjust } from "./elements/NavbarContainer";

// Sandwiches all content (mobile, tablet and desktop) with a navigation bar
export const NavbarWrapper = withIsVisible(({ children }: PropsWithChildren) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const isConnected = GlobalStore.useState(s => s.address) !== "";
  const location = useLocation();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceType.Mobile;
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const isActivityPage = (location.pathname as BEBOP_ROUTES) === BEBOP_ROUTES.Activity;
  const is404Page = !Object.values(BEBOP_ROUTES).includes(location.pathname as BEBOP_ROUTES);
  const isTradePage = location.pathname === BEBOP_ROUTES.Trade;
  const isTablet = useIsTablet();
  const BebopIcon = () =>
    isTablet ? (
      <BebopBWhite width={40} height={40} />
    ) : (
      <BebopWhiteIcon width={118} height={40} style={{ minWidth: "118px", minHeight: "40px" }} />
    );

  // Temporary for Jam waitlist
  const isJamTemporaryPages = location.pathname === "/signup" || location.pathname === "/waitlist";
  useToggleIntercom();

  // Do not show wrapper on home page
  if (location.pathname === "/" || isJamTemporaryPages) {
    return <>{children}</>;
  }

  if (is404Page) {
    return (
      <NotFoundPageContainer>
        <div
          css={css`
            padding-left: 135px;
            padding-top: 35px;
          `}
        >
          <Link to="/">
            <BebopWhiteIcon
              width={118}
              height={40}
              style={{ minWidth: "118px", minHeight: "40px" }}
            />
          </Link>
        </div>
        {children}
        <Background />
      </NotFoundPageContainer>
    );
  }
  if (!isMobile) {
    return (
      <>
        <Banner isVisible={isTradePage} />
        <ContainerDesktopOrTablet isActivityPage={isActivityPage} deviceType={deviceType}>
          <NavbarV2 />
          <HomeButton to="/">
            <BebopIcon />
          </HomeButton>
          {children}
          <Background />
        </ContainerDesktopOrTablet>
        <NonMobileSocialsContainer>
          <Socials />
        </NonMobileSocialsContainer>
      </>
    );
  }

  const profileBarBottomSheet = (
    <MobileBottomSheet
      title={isConnected ? "Wallet" : "Start trading now"}
      isOpen={isBottomSheetOpen}
      setIsOpen={setIsBottomSheetOpen}
    >
      <ProfileActionsContent closeDisplay={() => setIsBottomSheetOpen(false)} />
    </MobileBottomSheet>
  );

  return (
    <>
      <Banner isVisible={isTradePage} />
      <MobileContainer deviceType={deviceType}>
        <MobileContent>
          <MobileProfileBar setIsBottomSheetOpen={setIsBottomSheetOpen} />
          <MobileScrollableContainer>
            <MobileScrollableContent>{children}</MobileScrollableContent>
            <Background />
          </MobileScrollableContainer>
          {profileBarBottomSheet}
          <SideNav isOpen={isMoreOpen} setIsOpen={setIsMoreOpen} isMobileNavBarDisplayed={true} />
        </MobileContent>
        <NavbarV2Mobile isMoreOpen={isMoreOpen} setIsMoreOpen={setIsMoreOpen} />
      </MobileContainer>
    </>
  );
});

// Sub-components

const ContainerDesktopOrTablet = styled.div<{ isActivityPage: boolean; deviceType: DeviceType }>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5%;
  background-color: ${props => props.theme.theme["200"]};
  color: ${props => props.theme.font.primary.default};
  min-height: ${props => `calc(100dvh - ${heightAdjust(props.deviceType)}px)`};
  position: relative;
`;

const NotFoundPageContainer = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.theme["200"]};
  position: relative;
`;

const MobileContainer = styled.div<{ deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  width: 100dvw;
  height: ${props => `calc(100dvh - ${heightAdjust(props.deviceType)}px)`};
`;

const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.theme["200"]};
  height: calc(100dvh - ${MOBILE_NAVBAR_HEIGHT}px);
  overflow: hidden;
  position: relative;
`;

// MobileScrollableContainer + MobileScrollableContent:
// Hides scrollbar but still still allows for scrolling
const MobileScrollableContainer = styled.div`
  position: relative;
  width: 100%;
  height: fill-available;
  overflow-y: scroll;
  scrollbar-width: none;
  display: flex;
  justify-content: center;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const MobileScrollableContent = styled.div`
  width: fit-content;
  height: fit-content;
  padding-bottom: 20px;
  min-width: 340px;

  @media (max-width: 392px) {
    right: 0;
  }
`;

const NonMobileSocialsContainer = styled.div`
  position: fixed;
  right: 76px;
  bottom: 20px;
  z-index: ${Z_INDICES.SOCIALS};
`;

export const HomeButton = styled(Link)`
  position: fixed;
  bottom: 32px;
  left: 32px;
  z-index: ${Z_INDICES.NAV_BAR + 100};

  @media (max-width: ${TABLET_WIDTH}px) {
    left: 18px;
  }
`;

const Background = () => {
  return (
    <>
      <PositionedTradeBgTopRight />
      <PositionedTradeBgBottomLeft />
    </>
  );
};

const PositionedTradeBgTopRight = styled(TradeBgTopRight)`
  position: fixed;
  top: 0;
  right: 0;

  @media (max-width: ${TABLET_WIDTH}px) {
    transform: scale(0.8);
    transform-origin: top right;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    transform: scale(0.6);
    transform-origin: top right;
  }
`;

const PositionedTradeBgBottomLeft = styled(TradeBgBottomLeft)`
  position: fixed;
  bottom: 0;
  left: 0;

  @media (max-width: ${TABLET_WIDTH}px) {
    transform: scale(0.8);
    transform-origin: bottom left;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    bottom: ${MOBILE_NAVBAR_HEIGHT}px;
    transform: scale(0.6);
    transform-origin: bottom left;
  }
`;
